import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FilePenLine,
  CircleCheckBig,
  TriangleAlert,
  Clock,
  FileText,
  Plus,
  SquarePen,
  Paperclip,
  X,
} from "lucide-react";
import AsyncSelect from "react-select/async";
import * as helperService from "../../../services/helperService";
import { employeeService } from "../../../services/employeeService";
import { contractService } from "../../../services/contractService";

const buildEmptyForm = () => ({
  name: "",
  employee_id: null,
  team_member: "",
  agreement_date: "",
  renewal_date: "",
  status: "Pending",
  contract_file: "",
});

const debounce = (fn, delay = 300) => {
  let timeout;
  return (...args) =>
    new Promise((resolve) => {
      clearTimeout(timeout);
      timeout = setTimeout(async () => resolve(await fn(...args)), delay);
    });
};

const FilePreviewPanel = ({ preview, onClose }) => {
  if (!preview?.url) return null;

  return (
    <div className="mt-3 border border-canopy-border rounded-lg overflow-hidden bg-white">
      <div className="px-3 py-2 border-b border-canopy-border flex items-center justify-between">
        <span className="text-sm font-semibold text-canopy-text truncate">
          Preview: {preview.label}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="p-1 text-canopy-textMuted hover:text-canopy-text rounded transition"
          aria-label="Close preview"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {preview.type.startsWith("image/") && (
        <img
          src={preview.url}
          alt="File preview"
          className="max-h-96 w-auto object-contain mx-auto"
        />
      )}

      {preview.type === "application/pdf" && (
        <iframe title="PDF preview" src={preview.url} className="w-full h-96" />
      )}

      {(preview.type.startsWith("text/") ||
        preview.type === "application/json") && (
        <pre className="p-3 text-sm overflow-auto max-h-96 bg-gray-50">
          {preview.text ?? "—"}
        </pre>
      )}

      {!preview.type.startsWith("image/") &&
        preview.type !== "application/pdf" &&
        !preview.type.startsWith("text/") &&
        preview.type !== "application/json" && (
          <div className="p-3 text-sm text-canopy-textMuted">
            Preview not supported ({preview.type}).{" "}
            <button
              type="button"
              onClick={() => {
                const link = document.createElement("a");
                link.href = preview.url;
                link.download = preview.label || "file";
                link.click();
              }}
              className="text-canopy-blue hover:underline"
            >
              Download file
            </button>
          </div>
        )}
    </div>
  );
};

const ContractForm = ({
  title,
  submitLabel,
  formData,
  formError,
  contractFile,
  fileInputRef,
  onFileInputChange,
  onOpenFilePicker,
  onRemoveFile,
  onPreviewSelectedFile,
  onPreviewExistingFile,
  onGetFileHref,
  activeFilePreview,
  previewLoadingKey,
  onClosePreview,
  isSubmitting,
  onChange,
  onEmployeeChange,
  onCancel,
  onSubmit,
  employeeDefaults,
  loadEmployeeOptions,
}) => {
  const formPreview = activeFilePreview?.key?.startsWith("form-")
    ? activeFilePreview
    : null;

  const existingFileName = formData.contract_file
    ? formData.contract_file.split("/").pop() || formData.contract_file
    : "";

  return (
    <div className="border border-canopy-border rounded-xl p-5 bg-gray-50">
      <h3 className="text-2xl font-bold text-canopy-text mb-4">{title}</h3>

      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-canopy-text mb-2">
              Contract Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={onChange}
              className="w-full border border-canopy-border rounded-lg px-3 py-2 text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-canopy-text mb-2">
              Team Member
            </label>
            <AsyncSelect
              isClearable
              cacheOptions
              defaultOptions={employeeDefaults}
              loadOptions={loadEmployeeOptions}
              value={
                formData.employee_id
                  ? { value: formData.employee_id, label: formData.team_member }
                  : null
              }
              placeholder="Select a team member"
              onChange={onEmployeeChange}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-canopy-text mb-2">
              Agreement Date
            </label>
            <input
              type="date"
              name="agreement_date"
              value={formData.agreement_date}
              onChange={onChange}
              className="w-full border border-canopy-border rounded-lg px-3 py-2 text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-canopy-text mb-2">
              Renewal Date
            </label>
            <input
              type="date"
              name="renewal_date"
              value={formData.renewal_date}
              onChange={onChange}
              className="w-full border border-canopy-border rounded-lg px-3 py-2 text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-canopy-text mb-2">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={onChange}
              className="w-full border border-canopy-border rounded-lg px-3 py-2 text-sm"
              required
            >
              <option value="">Select status</option>
              <option value="Pending">Pending</option>
              <option value="Active">Active</option>
              <option value="Expired">Expired</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-canopy-text mb-2">
              Contract File
            </label>
            <div className="space-y-3">
              {!contractFile && !formData.contract_file && (
                <button
                  type="button"
                  onClick={onOpenFilePicker}
                  className="inline-flex items-center justify-center px-4 py-2 border border-dashed border-canopy-border text-canopy-textMuted hover:text-canopy-blue hover:border-canopy-blue rounded-lg transition-colors"
                >
                  <Paperclip className="h-4 w-4 mr-2" />
                  Upload File
                </button>
              )}

              <input
                ref={fileInputRef}
                type="file"
                onChange={onFileInputChange}
                className="hidden"
              />

              {formData.contract_file && !contractFile && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-canopy-textMuted">
                    Current File
                  </p>
                  <div className="flex flex-wrap items-center gap-2 text-sm text-canopy-textMuted">
                    <Paperclip className="h-4 w-4" />
                    <span className="break-all">{existingFileName}</span>
                    <button
                      type="button"
                      onClick={() =>
                        onPreviewExistingFile(
                          formData.contract_file,
                          "form-existing",
                          existingFileName,
                        )
                      }
                      className="text-xs px-2 py-1 rounded border border-canopy-border hover:bg-white transition"
                    >
                      {previewLoadingKey === "form-existing"
                        ? "Loading..."
                        : "Preview"}
                    </button>
                    <a
                      href={onGetFileHref(formData.contract_file)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-canopy-blue hover:underline"
                    >
                      Open
                    </a>
                    <button
                      type="button"
                      onClick={onOpenFilePicker}
                      className="text-xs text-canopy-blue hover:underline"
                    >
                      Replace
                    </button>
                  </div>
                </div>
              )}

              {contractFile && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-canopy-textMuted">
                    New Upload
                  </p>
                  <div className="flex flex-wrap items-center gap-2 text-sm text-canopy-textMuted">
                    <Paperclip className="h-4 w-4" />
                    <span className="break-all">{contractFile.name}</span>
                    <span className="text-xs text-canopy-textMuted">
                      • {helperService.formatBytes(contractFile.size)}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        onPreviewSelectedFile(
                          contractFile,
                          "form-new",
                          contractFile.name,
                        )
                      }
                      className="text-xs px-2 py-1 rounded border border-canopy-border hover:bg-white transition"
                    >
                      {previewLoadingKey === "form-new"
                        ? "Loading..."
                        : "Preview"}
                    </button>
                    <button
                      type="button"
                      onClick={onRemoveFile}
                      className="text-xs text-red-600 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              )}

              <FilePreviewPanel
                preview={formPreview}
                onClose={onClosePreview}
              />
            </div>
          </div>
        </div>

        {formError && (
          <p className="text-sm text-red-600 font-semibold">{formError}</p>
        )}

        <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-semibold rounded-lg border border-canopy-border hover:bg-white transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="canopy-btn-primary"
          >
            {isSubmitting ? "Saving..." : submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
};

const ContractsOverview = ({ userPermission }) => {
  const [contracts, setContracts] = useState([]);
  const [formMode, setFormMode] = useState(null);
  const [editingContractId, setEditingContractId] = useState(null);
  const [formData, setFormData] = useState(buildEmptyForm());
  const [contractFile, setContractFile] = useState(null);
  const fileInputRef = useRef(null);
  const [activeFilePreview, setActiveFilePreview] = useState(null);
  const [previewLoadingKey, setPreviewLoadingKey] = useState(null);
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [employeeDefaults, setEmployeeDefaults] = useState([]);
  const [activeFilter, setActiveFilter] = useState({ key: null, value: null });

  useEffect(() => {
    (async () => {
      try {
        const [{ items }, employees] = await Promise.all([
          contractService.getContracts(),
          employeeService.getManagers("", []),
        ]);
        setContracts(items);
        setEmployeeDefaults(
          employees.map((r) => ({ value: r.id, label: r.name })),
        );
      } catch (err) {
        console.error("Failed to load contracts", err);
      }
    })();
  }, []);

  useEffect(() => {
    return () => {
      if (activeFilePreview?.url) {
        URL.revokeObjectURL(activeFilePreview.url);
      }
    };
  }, [activeFilePreview]);

  const loadEmployeeOptions = async (inputValue) => {
    const rows = await employeeService.getManagers(inputValue, []);
    return rows.map((r) => ({ value: r.id, label: r.name }));
  };

  const debouncedLoadEmployees = useMemo(
    () => debounce(loadEmployeeOptions),
    [],
  );

  const handleEmployeeChange = (option) => {
    setFormData((prev) => ({
      ...prev,
      employee_id: option ? option.value : null,
      team_member: option ? option.label : "",
    }));
  };

  const statusStyles = {
    Pending: "bg-yellow-100 text-yellow-700",
    Active: "bg-green-100 text-green-700",
    Expired: "bg-red-100 text-red-700",
  };

  const stats = useMemo(() => {
    const totalContracts = contracts.length;
    const activeContracts = contracts.filter(
      (contract) => contract.status === "Active",
    ).length;
    const today = new Date();
    const renewalDue = contracts.filter(
      (contract) => new Date(contract.renewal_date) < today,
    ).length;
    const expiredContracts = contracts.filter(
      (contract) => contract.status === "Expired",
    ).length;

    return [
      {
        label: "Total Contracts",
        value: totalContracts,
        helper: "All contracts",
        icon: FilePenLine,
        helperClass: "text-canopy-blue",
        iconClass: "text-canopy-blue",
        filterKey: null,
        filterValue: null,
      },
      {
        label: "Active",
        value: activeContracts,
        helper: "Currently active",
        icon: CircleCheckBig,
        helperClass: "text-green-600",
        iconClass: "text-green-600",
        filterKey: "status",
        filterValue: "Active",
      },
      {
        label: "Renewal Due",
        value: renewalDue,
        helper: "Needs attention",
        icon: TriangleAlert,
        helperClass: "text-yellow-600",
        iconClass: "text-yellow-600",
        filterKey: "renewalDue",
        filterValue: "yes",
      },
      {
        label: "Expired",
        value: expiredContracts,
        helper: "Require action",
        icon: Clock,
        helperClass: "text-red-600",
        iconClass: "text-red-600",
        filterKey: "status",
        filterValue: "Expired",
      },
    ];
  }, [contracts]);

  const filteredContracts = useMemo(() => {
    if (!activeFilter.key) return contracts;
    if (activeFilter.key === "renewalDue") {
      const today = new Date();
      return contracts.filter((c) => new Date(c.renewal_date) < today);
    }
    return contracts.filter((c) => c[activeFilter.key] === activeFilter.value);
  }, [contracts, activeFilter]);

  const handleStatClick = (filterKey, filterValue) => {
    if (
      !filterKey ||
      (activeFilter.key === filterKey && activeFilter.value === filterValue)
    ) {
      setActiveFilter({ key: null, value: null });
    } else {
      setActiveFilter({ key: filterKey, value: filterValue });
    }
  };

  const closeFilePreview = () => {
    setActiveFilePreview((previous) => {
      if (previous?.url) URL.revokeObjectURL(previous.url);
      return null;
    });
    setPreviewLoadingKey(null);
  };

  const setPreviewFromBlob = async ({ key, label, blob, contentType }) => {
    const normalizedType =
      contentType || blob.type || "application/octet-stream";
    let text = null;
    if (
      normalizedType.startsWith("text/") ||
      normalizedType === "application/json"
    ) {
      try {
        text = await blob.text();
      } catch {
        text = null;
      }
    }
    const url = URL.createObjectURL(blob);
    setActiveFilePreview((previous) => {
      if (previous?.url) URL.revokeObjectURL(previous.url);
      return { key, label, url, type: normalizedType, text };
    });
  };

  const handlePreviewSelectedFile = async (file, key, label) => {
    setPreviewLoadingKey(key);
    setFormError("");
    try {
      await setPreviewFromBlob({
        key,
        label,
        blob: file,
        contentType: file.type || "application/octet-stream",
      });
    } catch (error) {
      setFormError("Unable to preview selected file.");
      console.error(error);
    } finally {
      setPreviewLoadingKey(null);
    }
  };

  const handlePreviewSavedFile = async (path, key, label) => {
    if (!path) return;
    setPreviewLoadingKey(key);
    setFormError("");
    try {
      const { blob, contentType } = await contractService.getContractFile(path);
      await setPreviewFromBlob({ key, label, blob, contentType });
    } catch (error) {
      setFormError("Unable to load file preview.");
      console.error(error);
    } finally {
      setPreviewLoadingKey(null);
    }
  };

  const handleOpenFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setContractFile(file);
    closeFilePreview();
    event.target.value = "";
  };

  const handleRemoveFile = () => {
    setContractFile(null);
    closeFilePreview();
  };

  const closeForm = () => {
    closeFilePreview();
    setFormMode(null);
    setEditingContractId(null);
    setFormData(buildEmptyForm());
    setContractFile(null);
    setIsSubmitting(false);
    setFormError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const openCreateForm = () => {
    closeFilePreview();
    setFormMode("create");
    setEditingContractId(null);
    setFormData(buildEmptyForm());
    setContractFile(null);
    setFormError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const openEditForm = (contract) => {
    closeFilePreview();
    setFormMode("edit");
    setEditingContractId(contract.id);
    setFormData({
      name: contract.name,
      employee_id: contract.employee_id ?? null,
      team_member: contract.team_member,
      agreement_date: contract.agreement_date,
      renewal_date: contract.renewal_date,
      status: contract.status,
      contract_file: contract.contract_file ?? "",
    });
    setContractFile(null);
    setFormError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setFormError("");

    const agreementDate = new Date(
      `${formData.agreement_date}T00:00:00`,
    ).getTime();
    const renewalDate = new Date(`${formData.renewal_date}T00:00:00`).getTime();

    if (renewalDate < agreementDate) {
      setFormError("Renewal date cannot be before agreement date.");
      return;
    }

    setIsSubmitting(true);
    try {
      let filePath = formData.contract_file || null;

      if (contractFile) {
        const uploaded = await contractService.uploadContractFile(contractFile);
        filePath = uploaded.path || null;
      }

      const payload = { ...formData, contract_file: filePath };

      if (formMode === "edit" && editingContractId) {
        const updated = await contractService.updateContract(
          editingContractId,
          payload,
        );
        setContracts((previous) =>
          previous.map((c) => (c.id === editingContractId ? updated : c)),
        );
      } else {
        const created = await contractService.createContract(payload);
        setContracts((previous) => [...previous, created]);
      }
      closeForm();
    } catch (err) {
      setFormError(err.message || "Failed to save contract.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const sharedFormProps = {
    formData,
    formError,
    contractFile,
    fileInputRef,
    onFileInputChange: handleFileInputChange,
    onOpenFilePicker: handleOpenFilePicker,
    onRemoveFile: handleRemoveFile,
    onPreviewSelectedFile: handlePreviewSelectedFile,
    onPreviewExistingFile: handlePreviewSavedFile,
    onGetFileHref: contractService.getContractFileUrl,
    activeFilePreview,
    previewLoadingKey,
    onClosePreview: closeFilePreview,
    isSubmitting,
    onChange: handleFormChange,
    onEmployeeChange: handleEmployeeChange,
    onCancel: closeForm,
    onSubmit: handleFormSubmit,
    employeeDefaults,
    loadEmployeeOptions: debouncedLoadEmployees,
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <section className="bg-white border border-canopy-border rounded-xl p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {stats.map((item) => {
            const Icon = item.icon;
            const isActive =
              item.filterKey === null
                ? activeFilter.key === null
                : activeFilter.key === item.filterKey &&
                  activeFilter.value === item.filterValue;
            return (
              <button
                key={item.label}
                type="button"
                onClick={() =>
                  handleStatClick(item.filterKey, item.filterValue)
                }
                className={`text-left border rounded-xl p-5 bg-white transition-colors ${
                  isActive
                    ? "border-canopy-blue ring-2 ring-canopy-blue/10"
                    : "border-canopy-border hover:border-canopy-blue/40"
                }`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-canopy-text">
                    {item.label}
                  </h3>
                  <Icon className={`h-4 w-4 ${item.iconClass}`} />
                </div>
                <p className="text-4xl font-bold text-canopy-text mt-3">
                  {item.value}
                </p>
                <p className={`text-sm mt-2 ${item.helperClass}`}>
                  {item.helper}
                </p>
              </button>
            );
          })}
        </div>
      </section>

      <section className="bg-white border border-canopy-border rounded-xl p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <h2 className="text-4xl/[1.1] sm:text-4xl font-bold text-canopy-text">
            Team Contracts
          </h2>

          {/* {isAdminView && ( */}
          <button
            type="button"
            onClick={openCreateForm}
            className="canopy-btn-primary inline-flex items-center justify-center gap-2 w-full md:w-auto"
          >
            <Plus className="w-6 h-6" />
          </button>
          {/* )} */}
        </div>

        {formMode === "create" && (
          <div className="mt-6">
            <ContractForm
              title="Create a Contract"
              submitLabel="Create"
              {...sharedFormProps}
            />
          </div>
        )}

        <div className="mt-6 space-y-4">
          {filteredContracts.map((contract) => {
            const isEditingThisContract =
              formMode === "edit" && editingContractId === contract.id;

            if (isEditingThisContract) {
              return (
                <div
                  key={contract.id}
                  className="border border-canopy-border rounded-xl p-5 bg-white"
                >
                  <ContractForm
                    title="Edit a Contract"
                    submitLabel="Save Changes"
                    {...sharedFormProps}
                  />
                </div>
              );
            }

            const fileLabel = contract.contract_file
              ? contract.contract_file.split("/").pop() ||
                contract.contract_file
              : null;
            const cardPreviewKey = `card-${contract.id}`;

            return (
              <div
                key={contract.id}
                className="border border-canopy-border rounded-xl p-5 bg-white"
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-red-600 text-white flex items-center justify-center font-semibold">
                      <FileText className="h-4 w-4" />
                    </div>

                    <div>
                      <p className="text-2xl font-bold text-canopy-text">
                        {contract.name}
                      </p>
                      <p className="text-sm text-canopy-textMuted">
                        Team Member: {contract.team_member}
                      </p>
                      <p className="text-sm text-canopy-textMuted mt-1">
                        Agreement:{" "}
                        {helperService.formatJoinedDate(
                          contract.agreement_date,
                        )}{" "}
                        • Renewal:{" "}
                        {helperService.formatJoinedDate(contract.renewal_date)}
                      </p>

                      {fileLabel && (
                        <div className="text-sm text-canopy-textMuted mt-1">
                          File:
                          <div className="mt-1 flex flex-wrap items-center gap-2">
                            <a
                              href={contractService.getContractFileUrl(
                                contract.contract_file,
                              )}
                              target="_blank"
                              rel="noreferrer"
                              className="text-canopy-blue hover:underline break-all"
                            >
                              {fileLabel}
                            </a>
                            <button
                              type="button"
                              onClick={() =>
                                handlePreviewSavedFile(
                                  contract.contract_file,
                                  cardPreviewKey,
                                  fileLabel,
                                )
                              }
                              className="text-xs px-2 py-1 rounded border border-canopy-border hover:bg-white transition"
                            >
                              {previewLoadingKey === cardPreviewKey
                                ? "Loading..."
                                : "Preview"}
                            </button>
                          </div>
                          {activeFilePreview?.key === cardPreviewKey && (
                            <FilePreviewPanel
                              preview={activeFilePreview}
                              onClose={closeFilePreview}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase ${
                        statusStyles[contract.status] ||
                        "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {contract.status}
                    </span>

                    <button
                      type="button"
                      onClick={() => openEditForm(contract)}
                      className="px-3 py-1 text-xs font-semibold rounded-lg border border-canopy-border hover:bg-gray-100 transition"
                    >
                      <SquarePen className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default ContractsOverview;
